import { useState } from 'react';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Select } from '../../components/ui/select';
import { Pagination } from '../../components/ui/Pagination';
import { Skeleton } from '../../components/ui/skeleton';
import { Table, Thead, Tbody, Tr, Th, Td } from '../../components/ui/table';
import { Search } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api-client';
import { formatDateTime } from '../../lib/utils';

interface AuditEntry {
  id: string;
  userId: string | null;
  action: string;
  entityType: string;
  entityId: string;
  meta: Record<string, unknown> | null;
  ipAddress: string | null;
  createdAt: string;
  user?: { name: string; email: string } | null;
}

interface AuditResponse {
  data: AuditEntry[];
  meta: { pagination: { page: number; totalPages: number; total: number } };
}

export function AuditPage() {
  const [page, setPage] = useState(1);
  const [entityType, setEntityType] = useState('');
  const [action, setAction] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['audit', page, entityType, action],
    queryFn: () => api.get<AuditEntry[]>(`/audit?page=${page}&pageSize=20${entityType ? `&entityType=${entityType}` : ''}${action ? `&action=${action}` : ''}`),
  });
  const entries = data?.data ?? [];
  const pagination = (data as unknown as AuditResponse)?.meta?.pagination;

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-dark-text">Jurnal de audit</h1>

      <Card className="p-4">
        <div className="flex flex-wrap gap-3">
          <Select
            options={[
              { value: '', label: 'Toate entitățile' },
              { value: 'TICKET', label: 'Tichete' },
              { value: 'USER', label: 'Utilizatori' },
              { value: 'COMMENT', label: 'Comentarii' },
              { value: 'SLA', label: 'SLA' },
              { value: 'TAG', label: 'Tag-uri' },
            ]}
            value={entityType}
            onChange={(e) => { setEntityType(e.target.value); setPage(1); }}
          />
          <Select
            options={[
              { value: '', label: 'Toate acțiunile' },
              { value: 'CREATE', label: 'Creare' },
              { value: 'UPDATE', label: 'Actualizare' },
              { value: 'DELETE', label: 'Ștergere' },
              { value: 'LOGIN', label: 'Autentificare' },
            ]}
            value={action}
            onChange={(e) => { setAction(e.target.value); setPage(1); }}
          />
        </div>
      </Card>

      <Card>
        <Table>
          <Thead>
            <Tr>
              <Th>Data</Th>
              <Th>Utilizator</Th>
              <Th>Acțiune</Th>
              <Th>Entitate</Th>
              <Th>ID Entitate</Th>
              <Th>IP</Th>
            </Tr>
          </Thead>
          <Tbody>
            {isLoading && Array.from({ length: 5 }).map((_, i) => (
              <Tr key={i}><Td colSpan={6}><Skeleton className="h-4 w-full" /></Td></Tr>
            ))}
            {!isLoading && entries.length === 0 && (
              <Tr><Td colSpan={6} className="text-center text-gray-400">Nu s-au găsit înregistrări</Td></Tr>
            )}
            {entries.map((entry) => (
              <Tr key={entry.id}>
                <Td className="text-sm text-gray-500 whitespace-nowrap">{formatDateTime(entry.createdAt)}</Td>
                <Td className="text-sm">{entry.user?.name ?? '—'}</Td>
                <Td className="text-sm font-medium">{entry.action}</Td>
                <Td className="text-sm text-gray-500">{entry.entityType}</Td>
                <Td className="text-sm text-gray-400 font-mono">{entry.entityId.slice(0, 8)}...</Td>
                <Td className="text-sm text-gray-400">{entry.ipAddress ?? '—'}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        {pagination && <Pagination page={pagination.page} totalPages={pagination.totalPages} onPageChange={setPage} />}
      </Card>
    </div>
  );
}
